/**
 * @file CustomSearchInput.tsx
 * @module app/custom/search/CustomSearchInput
 * @description Renders and exports the CustomSearchInput reusable search-input component for filtering data collections.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.4.1
 */

import React, { useEffect, useRef, useState } from "react";
import Search from "../../../_admin/assets/media/svg_uTrade/search.svg";
import { useThemeMode } from "../../../_admin/partials";

interface SearchInputProps {
  onSearch: (value: string) => void;
  placeholder?: string;
  defaultValue?: string;
  delay?: number;
  className?: string;
  backgroundColor?: string;
}

const CustomSearchInput: React.FC<SearchInputProps> = ({
  onSearch,
  placeholder = "Search...",
  defaultValue = "",
  delay = 500,
  className = "",
  backgroundColor,
}) => {
  const [inputValue, setInputValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const { mode } = useThemeMode();
  const isDark = mode === "dark";

  useEffect(() => {
    if (inputRef.current) {
      // Skip forcing a light background in dark mode so the global dark theme styles can apply
      if (backgroundColor && !isDark) {
        inputRef.current.style.setProperty(
          "background-color",
          backgroundColor,
          "important",
        );
      } else {
        inputRef.current.style.removeProperty("background-color");
      }
    }
  }, [backgroundColor, isDark]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    onSearch(value);
  };

  return (
    <div className="d-flex gap-2 align-items-center">
      <div
        className="symbol symbol-25px position-absolute ps-3"
        data-kt-initialized="1"
      >
        <img
          src={Search}
          alt="search-icon"
          className=""
          style={{ width: "18px", height: "18px", color: "#a1a5b7" }}
        />
      </div>

      <input
        ref={inputRef}
        type="text"
        className={`form-control form-control-custom borde-r8px bg-light ps-11 fs-15 fw-500 ${className}`}
        placeholder={placeholder}
        value={inputValue}
        onChange={handleSearch}
      />
    </div>
  );
};

export default CustomSearchInput;
